import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector,
} from 'recharts';
import EmptyState from '../shared/EmptyState';
import { sk } from '../shared/skeletonClass';
import type { DispositionSlice } from '@types';

interface DispositionPieChartProps {
  data: DispositionSlice[];
  isLoading: boolean;
}

interface ActiveSectorProps {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  fill?: string;
}

// Renders the focused slice slightly larger so the corresponding sector is
// visibly highlighted when the user hovers either the pie or the legend.
function renderActiveSector (props: ActiveSectorProps): React.JSX.Element {
  const { cx, cy, innerRadius, outerRadius = 0, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
}

export default function DispositionPieChart ({ data, isLoading }: DispositionPieChartProps): React.JSX.Element {
  const { t } = useTranslation();

  const getDispositionLabel = (disposition: string): string => {
    const key = `dashboard.overview.dispositionBreakdown.${disposition}`;
    const translated = t(key);
    return translated !== key ? translated : disposition.replace(/_/g, ' ');
  };
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const onPieEnter = useCallback((_: unknown, index: number) => {
    setActiveIndex(index);
  }, []);

  const onPieLeave = useCallback(() => {
    setActiveIndex(undefined);
  }, []);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card">
        <div className="p-5 pb-3">
          <div className={`h-5 w-44 mb-1.5 ${sk}`} />
          <div className={`h-3 w-52 ${sk}`} />
        </div>
        <div className="px-5 pb-5 flex items-start gap-4">
          <div className={`h-[200px] w-[200px] rounded-full flex-shrink-0 ${sk}`} />
          <div className="flex flex-col gap-2.5 pt-3 flex-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${sk}`} />
                <div className={`h-3 flex-1 ${sk}`} />
                <div className={`h-3 w-16 ${sk}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="p-5 pb-3">
        <h3 className="text-base font-semibold text-foreground">{t('dashboard.overview.dispositionBreakdown.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('dashboard.overview.dispositionBreakdown.description')}</p>
      </div>
      <div className="px-5 pb-5">
        {data.length === 0 && (
          <EmptyState message={t('dashboard.overview.dispositionBreakdown.empty')} />
        )}
        {data.length > 0 && <div className="flex items-start gap-4">
          {/* Donut Chart */}
          <div className="relative h-[220px] w-[220px] flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="disposition"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={88}
                  strokeWidth={2}
                  stroke="hsl(var(--card))"
                  activeIndex={activeIndex}
                  activeShape={renderActiveSector}
                  onMouseEnter={onPieEnter}
                  onMouseLeave={onPieLeave}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.color}
                      fillOpacity={activeIndex !== undefined && activeIndex !== index ? 0.35 : 1}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-foreground">{total.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">{t('dashboard.overview.dispositionBreakdown.totalCalls')}</span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-2 pt-3 min-w-0">
            {data.map((slice, index) => (
              <div
                key={slice.disposition}
                className={`flex items-center gap-2 text-xs transition-opacity ${
                  activeIndex !== undefined && activeIndex !== index ? 'opacity-40' : ''
                }`}
                onMouseEnter={() => {
                  setActiveIndex(index);
                }}
                onMouseLeave={() => {
                  setActiveIndex(undefined);
                }}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="text-muted-foreground truncate">
                  {getDispositionLabel(slice.disposition)}
                </span>
                <span className="ml-auto font-medium tabular-nums whitespace-nowrap text-foreground">
                  {slice.count} ({slice.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>}
      </div>
    </div>
  );
}
